import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import { Box, Button, TextField, Radio, RadioGroup, FormControlLabel, Divider, Typography } from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import RepeatIcon from '@mui/icons-material/Repeat';
import SimpleAutoComplete from '../auto-complete/autocomplete';

// Editor de agendamento CONTROLADO (value + onChange(patch)), extraído do wizard do Marketing para
// reuso no Agendamento de Relatórios. Shape da recorrência = ScheduleRecurrence do backend
// (Mexx.Shared.Domain.Scheduling) — o mesmo JSON do Marketing.
export const TIMEZONES = [
  { id: 'America/Sao_Paulo', name: 'America/Sao_Paulo (GMT-3)' },
  { id: 'America/Manaus', name: 'America/Manaus (GMT-4)' },
  { id: 'America/New_York', name: 'America/New_York (GMT-5)' },
  { id: 'Europe/Lisbon', name: 'Europe/Lisbon (GMT+0)' },
];

export const DEFAULT_RECURRENCE = {
  freq: 'weekly', time: '08:00', interval: 1, weekdays: [1],
  monthlyMode: 'dayOfMonth', dayOfMonth: 1, nth: 1, nthWeekday: 1,
  endMode: 'never', endCount: 5, endDate: '',
};

const WEEKDAY_IDS = [0, 1, 2, 3, 4, 5, 6];

// Resumo legível ("Toda semana (segunda-feira) às 08:00 · até 5 execuções"), 100% i18n.
export const buildScheduleSummary = (intl, value = {}) => {
  const f = (id, v) => intl.formatMessage({ id }, v);
  if (value.scheduleMode === 'once') {
    return value.onceAt ? f('report.schedule.summary.once', { datetime: moment(value.onceAt).format('DD/MM/YYYY HH:mm') }) : '—';
  }
  const r = value.recurrence || DEFAULT_RECURRENCE;
  const n = Math.max(1, Number(r.interval) || 1);
  let base;
  if (r.freq === 'daily') base = f('report.schedule.summary.daily', { n, time: r.time });
  else if (r.freq === 'weekly') {
    const days = (r.weekdays || []).length ? r.weekdays.map(d => f(`report.schedule.weekday.${d}`)).join(', ') : '—';
    base = f('report.schedule.summary.weekly', { n, days, time: r.time });
  }
  else if (r.monthlyMode === 'nthWeekday') {
    base = f('report.schedule.summary.monthly.nth', { nth: f(`report.schedule.nth.${r.nth}`), weekday: f(`report.schedule.weekday.${r.nthWeekday}`), time: r.time });
  }
  else base = f('report.schedule.summary.monthly.day', { day: r.dayOfMonth, time: r.time });

  let end = '';
  if (r.endMode === 'count' && r.endCount) end = f('report.schedule.summary.end.count', { n: r.endCount });
  else if (r.endMode === 'date' && r.endDate) end = f('report.schedule.summary.end.date', { date: moment(r.endDate).format('DD/MM/YYYY') });
  return base + end;
};

class RecurrenceEditor extends Component {
  patch = (p) => this.props.onChange(p);
  patchRecurrence = (p) => this.props.onChange({ recurrence: { ...(this.props.value.recurrence || DEFAULT_RECURRENCE), ...p } });

  toggleWeekday = (id) => {
    const cur = (this.props.value.recurrence || DEFAULT_RECURRENCE).weekdays || [];
    this.patchRecurrence({ weekdays: cur.includes(id) ? cur.filter(d => d !== id) : [...cur, id].sort() });
  };

  modeCard(mode, titleId, descId, icon) {
    const { intl, value } = this.props;
    const active = value.scheduleMode === mode;
    return (
      <Box onClick={() => this.patch({ scheduleMode: mode })} sx={{
        flex: 1, minWidth: 150, cursor: 'pointer', p: 1.5, borderRadius: '10px',
        border: active ? '2px solid #8BC34A' : '1px solid #e0e0e0',
        backgroundColor: active ? '#f3fae9' : '#fff', transition: 'all .2s',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {React.cloneElement(icon, { sx: { color: active ? '#5b9b1e' : '#9e9e9e', fontSize: 20 } })}
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: active ? '#5b9b1e' : '#444' }}>{intl.formatMessage({ id: titleId })}</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>{intl.formatMessage({ id: descId })}</Typography>
      </Box>
    );
  }

  freqButton(freq, labelId) {
    const { intl, value } = this.props;
    const active = (value.recurrence || DEFAULT_RECURRENCE).freq === freq;
    return (
      <Button onClick={() => this.patchRecurrence({ freq })} variant={active ? 'contained' : 'outlined'} size="small"
        sx={{ textTransform: 'none', backgroundColor: active ? '#8BC34A' : 'transparent', color: active ? '#fff' : '#555', borderColor: '#cfcfcf', '&:hover': { backgroundColor: active ? '#7cb342' : 'rgba(0,0,0,0.03)' } }}>
        {intl.formatMessage({ id: labelId })}
      </Button>
    );
  }

  renderRecurrenceBuilder() {
    const { intl } = this.props;
    const r = this.props.value.recurrence || DEFAULT_RECURRENCE;
    const timeField = (
      <TextField type="time" size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 130 }}
        label={intl.formatMessage({ id: 'marketing.schedule.time' })} value={r.time} onChange={e => this.patchRecurrence({ time: e.target.value })} />
    );
    const intervalField = (label) => (
      <TextField type="number" size="small" InputLabelProps={{ shrink: true }} sx={{ width: 130 }} inputProps={{ min: 1 }}
        label={label} value={r.interval} onChange={e => this.patchRecurrence({ interval: Math.max(1, Number(e.target.value)) })} />
    );
    return (
      <Box sx={{ mt: 1.5, p: 2, border: '1px solid #e8e8e8', borderRadius: '10px', backgroundColor: '#fafafa' }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {this.freqButton('daily', 'marketing.schedule.freq.daily')}
          {this.freqButton('weekly', 'marketing.schedule.freq.weekly')}
          {this.freqButton('monthly', 'marketing.schedule.freq.monthly')}
        </Box>

        {r.freq === 'daily' && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {intervalField(intl.formatMessage({ id: 'marketing.schedule.everyDays' }))}
            {timeField}
          </Box>
        )}

        {r.freq === 'weekly' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {intervalField(intl.formatMessage({ id: 'marketing.schedule.everyWeeks' }))}
              {timeField}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.7 }}>
              {WEEKDAY_IDS.map(d => {
                const on = (r.weekdays || []).includes(d);
                return (
                  <Box key={d} onClick={() => this.toggleWeekday(d)} title={intl.formatMessage({ id: `report.schedule.weekday.${d}` })} sx={{
                    width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    fontSize: 13, fontWeight: 700, transition: 'all .15s',
                    backgroundColor: on ? '#8BC34A' : '#fff', color: on ? '#fff' : '#888', border: on ? '1px solid #8BC34A' : '1px solid #ddd',
                  }}>{intl.formatMessage({ id: `report.schedule.weekday.short.${d}` })}</Box>
                );
              })}
            </Box>
          </Box>
        )}

        {r.freq === 'monthly' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <RadioGroup value={r.monthlyMode} onChange={e => this.patchRecurrence({ monthlyMode: e.target.value })}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <FormControlLabel value="dayOfMonth" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.schedule.onDay' })} />
                <TextField type="number" size="small" InputLabelProps={{ shrink: true }} sx={{ width: 90 }} inputProps={{ min: 1, max: 31 }}
                  disabled={r.monthlyMode !== 'dayOfMonth'} value={r.dayOfMonth} onChange={e => this.patchRecurrence({ dayOfMonth: Math.min(31, Math.max(1, Number(e.target.value))) })} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                <FormControlLabel value="nthWeekday" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.schedule.onThe' })} />
                <TextField select size="small" SelectProps={{ native: true }} sx={{ width: 130 }} disabled={r.monthlyMode !== 'nthWeekday'}
                  value={r.nth} onChange={e => this.patchRecurrence({ nth: Number(e.target.value) })}>
                  <option value={1}>{intl.formatMessage({ id: 'marketing.schedule.nth.1' })}</option>
                  <option value={2}>{intl.formatMessage({ id: 'marketing.schedule.nth.2' })}</option>
                  <option value={3}>{intl.formatMessage({ id: 'marketing.schedule.nth.3' })}</option>
                  <option value={4}>{intl.formatMessage({ id: 'marketing.schedule.nth.4' })}</option>
                  <option value={-1}>{intl.formatMessage({ id: 'marketing.schedule.nth.last' })}</option>
                </TextField>
                <TextField select size="small" SelectProps={{ native: true }} sx={{ width: 150 }} disabled={r.monthlyMode !== 'nthWeekday'}
                  value={r.nthWeekday} onChange={e => this.patchRecurrence({ nthWeekday: Number(e.target.value) })}>
                  {WEEKDAY_IDS.map(i => <option key={i} value={i}>{intl.formatMessage({ id: `report.schedule.weekday.${i}` })}</option>)}
                </TextField>
              </Box>
            </RadioGroup>
            <Box>{timeField}</Box>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>{intl.formatMessage({ id: 'marketing.schedule.ends' })}</Typography>
        <RadioGroup value={r.endMode || 'never'} onChange={e => this.patchRecurrence({ endMode: e.target.value })}>
          <FormControlLabel value="never" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.schedule.ends.never' })} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControlLabel value="count" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.schedule.ends.afterN' })} />
            <TextField type="number" size="small" sx={{ width: 90 }} inputProps={{ min: 1 }} disabled={r.endMode !== 'count'}
              value={r.endCount} onChange={e => this.patchRecurrence({ endCount: Math.max(1, Number(e.target.value)) })} />
            <Typography sx={{ fontSize: 13, color: '#666' }}>{intl.formatMessage({ id: 'marketing.schedule.ends.executions' })}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControlLabel value="date" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.schedule.ends.onDate' })} />
            <TextField type="date" size="small" InputLabelProps={{ shrink: true }} sx={{ width: 180 }} disabled={r.endMode !== 'date'}
              value={r.endDate} onChange={e => this.patchRecurrence({ endDate: e.target.value })} />
          </Box>
        </RadioGroup>
      </Box>
    );
  }

  render() {
    const { intl, value } = this.props;
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
          {this.modeCard('recurring', 'report.schedule.mode.recurring', 'report.schedule.mode.recurring.desc', <RepeatIcon />)}
          {this.modeCard('once', 'report.schedule.mode.once', 'report.schedule.mode.once.desc', <EventOutlinedIcon />)}
        </Box>

        {value.scheduleMode === 'once' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
            <TextField type="datetime-local" size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 260 }}
              label={intl.formatMessage({ id: 'marketing.schedule.datetime' })} value={value.onceAt || ''} onChange={e => this.patch({ onceAt: e.target.value })} />
            {/* +1 min: o passo exige data futura e o motor passa a cada 60 s → dispara no próximo ciclo. */}
            <Button variant="outlined" size="small" onClick={() => this.patch({ onceAt: moment().add(1, 'minute').format('YYYY-MM-DDTHH:mm') })}
              sx={{ textTransform: 'none', borderColor: '#8BC34A', color: '#558B2F' }}>
              {intl.formatMessage({ id: 'report.schedule.once.applyNow' })}
            </Button>
          </Box>
        )}

        {value.scheduleMode === 'recurring' && this.renderRecurrenceBuilder()}

        <Box sx={{ mt: 2, maxWidth: 300 }}>
          <SimpleAutoComplete label={intl.formatMessage({ id: 'marketing.schedule.timezone' })} options={TIMEZONES} size="small" stateName="timezone"
            selected={TIMEZONES.find(o => o.id === value.timezone) || { id: 0, name: '' }}
            changeSelect={(n, v) => this.patch({ timezone: v && v.id ? v.id : '' })} />
        </Box>

        <Box sx={{ mt: 2, p: 1.2, px: 1.5, borderRadius: '8px', backgroundColor: '#eef7e3', border: '1px solid #cfe8bf', display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventOutlinedIcon sx={{ fontSize: 18, color: '#5b9b1e' }} />
          <Typography sx={{ fontSize: 13, color: '#5b9b1e', fontWeight: 600 }}>{buildScheduleSummary(intl, value)}</Typography>
        </Box>
      </Box>
    );
  }
}

export default injectIntl(RecurrenceEditor);
